/**
 * Page Profil — agrège User Service + School Service.
 * Enveloppe alignée sur FormPageLayout (fil d’Ariane + zone principale scrollable).
 */
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@sphere/auth/data-access';
import { useMyCompleteProfile } from '@sphere/user/data-access';
import {
  InlineNotification,
  Button,
  Loading,
  Breadcrumb,
  BreadcrumbItem,
  Tag,
  SkeletonText,
} from '@carbon/react';
import { Security, Renew, Email, Logout, Settings } from '@carbon/icons-react';
import type { UserDetailWithCapabilities } from '@sphere/user/domain';
import { formatDateTimeFrShort } from '@sphere/shared/util';

import { ProfileStatsPanel } from './components/profile-stats-panel';
import { ProfileEstablishments } from './components/profile-establishments';
import { ProfileClasses } from './components/profile-classes';
import { ProfileGroups } from './components/profile-groups';
import styles from './profile-page.module.scss';

function profilePrimaryName(user: UserDetailWithCapabilities): string {
  const fromParts = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (fromParts) return fromParts;
  return user.displayName?.trim() || user.username;
}

function profileInitials(user: UserDetailWithCapabilities): string {
  if (user.firstName && user.lastName) {
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  }
  const base = user.displayName || user.username || '?';
  return base.slice(0, 2).toUpperCase();
}

function ProfileHeroBar({
  user,
  onLogout,
}: {
  user: UserDetailWithCapabilities;
  onLogout: () => void;
}) {
  const primary = profilePrimaryName(user);
  const alias =
    user.displayName?.trim() &&
    user.displayName.trim().toLowerCase() !== primary.toLowerCase()
      ? user.displayName.trim()
      : null;

  return (
    <div className={styles.heroProfileBar}>
      <div className={styles.heroProfileMain}>
        <div className={styles.heroAvatar}>
          {user.avatarUrl ? (
            <img src={user.avatarUrl} alt="" />
          ) : (
            <span className={styles.heroAvatarPlaceholder}>{profileInitials(user)}</span>
          )}
        </div>

        <div className={styles.heroText}>
          <div className={styles.heroTitleRow}>
            <h1 className={styles.heroName}>{primary}</h1>
            <Tag type={user.status === 'ACTIVE' ? 'green' : 'gray'}>
              {user.status === 'ACTIVE' ? 'Actif' : user.status}
            </Tag>
          </div>
          {alias ? <p className={styles.heroDisplayAlias}>{alias}</p> : null}
          {user.email ? (
            <p className={styles.heroEmail}>
              <Email size={16} aria-hidden />
              <span>{user.email}</span>
            </p>
          ) : null}
          <p className={styles.heroUsername}>@{user.username}</p>
        </div>
      </div>

      <div className={styles.heroActions}>
        <Button
          kind="tertiary"
          size="sm"
          as={Link}
          to="/settings"
          renderIcon={Settings}
          iconDescription="Paramètres"
        >
          Paramètres
        </Button>
        <Button kind="tertiary" size="sm" onClick={onLogout}>
          <Logout size={16} style={{ marginRight: 8 }} />
          Déconnexion
        </Button>
      </div>
    </div>
  );
}

function ProfileHeroSkeleton() {
  return (
    <div className={styles.heroProfileBar}>
      <div className={styles.heroSkeletonMain}>
        <div
          className="cds--skeleton cds--skeleton--text"
          style={{
            height: '4rem',
            width: '4rem',
            borderRadius: '50%',
            flexShrink: 0,
          }}
        />
        <div className={styles.heroSkeletonText}>
          <SkeletonText width="14rem" heading />
          <SkeletonText width="20rem" />
          <SkeletonText width="16rem" />
        </div>
      </div>
      <div className={styles.heroSkeletonActions}>
        <div
          className="cds--skeleton cds--skeleton--text"
          style={{ height: '2rem', width: '6.5rem' }}
        />
        <div
          className="cds--skeleton cds--skeleton--text"
          style={{ height: '2rem', width: '7.5rem' }}
        />
      </div>
    </div>
  );
}

function ProfilePageChrome({
  children,
  heroBelowBreadcrumb,
}: {
  children: ReactNode;
  heroBelowBreadcrumb?: ReactNode;
}) {
  const navigate = useNavigate();

  return (
    <div className={styles.pageContainer}>
      <header className={styles.heroHeader}>
        <div className={styles.breadcrumbs}>
          <Breadcrumb noTrailingSlash>
            <BreadcrumbItem onClick={() => navigate('/')}>
              <span className={styles.breadcrumbLink}>Accueil</span>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <span>Profil</span>
            </BreadcrumbItem>
          </Breadcrumb>
        </div>
        {heroBelowBreadcrumb ?? (
          <div className={styles.titleSection}>
            <h1>Profil</h1>
            <p>Votre compte et votre scolarité.</p>
          </div>
        )}
      </header>
      {children}
    </div>
  );
}

export function ProfilePage() {
  const { ready, authenticated, user: userInfo, logout } = useAuth();
  const navigate = useNavigate();
  const {
    data: profile,
    isLoading,
    isError,
    error,
    refetch,
  } = useMyCompleteProfile({
    enabled: authenticated,
  });

  if (!ready) {
    return (
      <ProfilePageChrome>
        <div className={styles.mainLayout}>
          <div className={styles.centeredState}>
            <Loading
              description="Initialisation de la session..."
              withOverlay={false}
            />
          </div>
        </div>
      </ProfilePageChrome>
    );
  }

  if (!authenticated) {
    return (
      <ProfilePageChrome>
        <div className={styles.mainLayout}>
          <section className={styles.unauthPanel} aria-labelledby="unauth-heading">
            <h2 id="unauth-heading" className={styles.unauthTitle}>
              Non authentifié
            </h2>
            <p className={styles.unauthText}>
              Vous devez être connecté pour accéder à cette page.
            </p>
            <Button onClick={() => navigate('/')}>Retour à l&apos;accueil</Button>
          </section>
        </div>
      </ProfilePageChrome>
    );
  }

  if (isLoading) {
    return (
      <ProfilePageChrome heroBelowBreadcrumb={<ProfileHeroSkeleton />}>
        <div className={styles.mainLayout}>
          <div className={styles.mainInner}>
            <ProfileSkeleton />
          </div>
        </div>
      </ProfilePageChrome>
    );
  }

  if (isError) {
    return (
      <ProfilePageChrome>
        <div className={styles.mainLayout}>
          <div className={styles.mainInner}>
            <InlineNotification kind="error" title="Erreur de chargement" lowContrast>
              <p>Impossible de charger votre profil.</p>
              <p className={styles.errorDetail}>
                {error?.message || 'Une erreur inattendue est survenue.'}
              </p>
              <Button
                kind="tertiary"
                size="sm"
                className={styles.errorRetry}
                onClick={() => refetch()}
                renderIcon={Renew}
              >
                Réessayer
              </Button>
            </InlineNotification>
          </div>
        </div>
      </ProfilePageChrome>
    );
  }

  if (!profile) {
    return null;
  }

  const rolesToDisplay = (() => {
    const roles = userInfo?.roles;
    if (!roles) return [];
    if (Array.isArray(roles)) return roles;
    if (typeof roles === 'object' && roles !== null) {
      const r = roles as { realm?: string[]; client?: string[] };
      return [...(r.realm || []), ...(r.client || [])];
    }
    return [];
  })();

  return (
    <ProfilePageChrome
      heroBelowBreadcrumb={
        <ProfileHeroBar user={profile.user} onLogout={logout} />
      }
    >
      <div className={styles.mainLayout}>
        <div className={styles.mainInner}>
          <ProfileStatsPanel stats={profile.school.stats} />

          <div className={styles.grid}>
            <div className={styles.mainCol}>
              <ProfileClasses classes={profile.school.classes} />

              {profile.school.groups.length > 0 && (
                <ProfileGroups groups={profile.school.groups} />
              )}
            </div>

            <div className={styles.sidebar}>
              <ProfileEstablishments
                establishments={profile.school.establishments}
              />

              {rolesToDisplay.length > 0 && (
                <aside className={styles.sideCard} aria-labelledby="profile-roles-heading">
                  <div className={styles.sideCardHeader}>
                    <Security size={16} aria-hidden className={styles.sideCardHeaderIcon} />
                    <h2 id="profile-roles-heading" className={styles.sideCardTitle}>
                      Rôles système
                    </h2>
                  </div>
                  <div className={styles.rolesList}>
                    {rolesToDisplay.map((role: string) => (
                      <Tag key={role} type="gray">
                        {role}
                      </Tag>
                    ))}
                  </div>
                </aside>
              )}
            </div>
          </div>

          <div className={styles.footer}>
            Dernière mise à jour:{' '}
            {formatDateTimeFrShort(profile.meta.fetchedAt)}
          </div>
        </div>
      </div>
    </ProfilePageChrome>
  );
}

function ProfileSkeleton() {
  return (
    <div className={styles.skeletonContent}>
      <div className={styles.skeletonStatsWrap}>
        <div className={styles.skeletonStatsRow}>
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="cds--skeleton cds--skeleton--text"
              style={{ height: '4.25rem', width: '100%' }}
            />
          ))}
        </div>
      </div>

      <div className={styles.skeletonGrid}>
        <div className={styles.mainCol}>
          <div className={styles.skeletonPanel}>
            <SkeletonText width="12rem" heading />
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
            >
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="cds--skeleton cds--skeleton--text"
                  style={{ height: '5rem' }}
                />
              ))}
            </div>
          </div>
        </div>
        <div className={styles.sidebar}>
          <div className={styles.skeletonPanel}>
            <SkeletonText width="8rem" heading />
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
            >
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="cds--skeleton cds--skeleton--text"
                  style={{ height: '4rem' }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
